import {
  BoxSize,
  CarrierService,
  FulfillmentLineExceptionType,
  PackOrder,
  PackQueueItem,
  PackSession,
} from './types';
import {
  BOX_SIZES,
  MOCK_ORDERS,
  MOCK_QUEUE,
  MOCK_SESSIONS,
  MOCK_SHIPPO_BASE_RATES,
} from './packStubs';

export const USE_STUBS = true;

const delay = (ms = 200) => new Promise<void>(res => setTimeout(res, ms));

export type ScanResult =
  | { status: 'ok'; gid: string }
  | { status: 'already_packed'; gid: string; boxId: string }
  | { status: 'exception'; gid: string; exceptionType: FulfillmentLineExceptionType }
  | { status: 'not_found' };

export function matchScan(order: PackOrder, session: PackSession, scanValue: string): ScanResult {
  const v = scanValue.trim();
  const line = order.lines.find(l => l.sku === v || l.upc === v);
  if (!line) return { status: 'not_found' };
  const state = session.lineStates[line.shopifyLineItemGid];
  if (state?.exceptionType) {
    return {
      status: 'exception',
      gid: line.shopifyLineItemGid,
      exceptionType: state.exceptionType,
    };
  }
  if (state?.boxId) {
    return { status: 'already_packed', gid: line.shopifyLineItemGid, boxId: state.boxId };
  }
  return { status: 'ok', gid: line.shopifyLineItemGid };
}

export async function getPackQueue(): Promise<PackQueueItem[]> {
  if (USE_STUBS) {
    await delay();
    return [...MOCK_QUEUE];
  }
  throw new Error('Not implemented');
}

export async function getPackOrder(orderId: number): Promise<PackOrder | null> {
  if (USE_STUBS) {
    await delay();
    return MOCK_ORDERS.find(o => o.shopifyOrderId === orderId) ?? null;
  }
  throw new Error('Not implemented');
}

export async function getOrCreateSession(order: PackOrder): Promise<PackSession> {
  if (USE_STUBS) {
    await delay();
    if (MOCK_SESSIONS[order.shopifyOrderId]) {
      return { ...MOCK_SESSIONS[order.shopifyOrderId] };
    }
    const hasBike = order.lines.some(l => l.itemType === 'bike' || l.itemType === 'frame');
    const bikeBoxInfo = BOX_SIZES.find(s => s.size.startsWith('Bike'))!;
    const initialBoxes = hasBike
      ? [{ id: 'box-1', size: bikeBoxInfo.size as BoxSize, tareLbs: bikeBoxInfo.tareLbs }]
      : [];
    return {
      orderId: order.shopifyOrderId,
      boxes: initialBoxes,
      labels: {},
      slipsPrinted: {},
      addressConfirmed: false,
      lineStates: Object.fromEntries(
        order.lines.map(l => [
          l.shopifyLineItemGid,
          { boxId: null, exceptionType: null, exceptionReason: null },
        ]),
      ),
    };
  }
  throw new Error('Not implemented');
}

export async function searchOrdersBySku(scanValue: string): Promise<PackOrder[]> {
  if (USE_STUBS) {
    await delay();
    const v = scanValue.trim();
    return MOCK_ORDERS.filter(
      o => o.lifecycleStatus === 'picked' && o.lines.some(l => l.sku === v || l.upc === v),
    );
  }
  throw new Error('Not implemented');
}

export async function getShippoRates(
  weightLbs: number,
): Promise<Record<CarrierService, { cost: number; transitDays: string }>> {
  if (USE_STUBS) {
    await delay(400);
    return Object.fromEntries(
      Object.entries(MOCK_SHIPPO_BASE_RATES).map(([service, r]) => [
        service,
        { cost: +(r.baseCost + r.perLbCost * weightLbs).toFixed(2), transitDays: r.transitDays },
      ]),
    ) as Record<CarrierService, { cost: number; transitDays: string }>;
  }
  throw new Error('Not implemented');
}

const shipAttempts: Record<number, number> = {};

export async function shipOrder(orderId: number): Promise<{ success: boolean; error?: string }> {
  if (USE_STUBS) {
    await delay(900);
    shipAttempts[orderId] = (shipAttempts[orderId] ?? 0) + 1;
    const order = MOCK_ORDERS.find(o => o.shopifyOrderId === orderId);
    if (order?.hasAddressError && shipAttempts[orderId] === 1) {
      return { success: false, error: 'Carrier rejected shipment: address could not be verified.' };
    }
    return { success: true };
  }
  throw new Error('Not implemented');
}
